import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  createProductList,
  deleteProductList,
  getProductList,
  importProductListItems,
  previewImportFile,
  searchProducts,
  setProductListItems,
  updateProductList,
} from '../../services/purchaseProgramApi';
import ConfirmPopup from './ConfirmPopup';

const IMPORT_FIELD_OPTIONS = [
  { value: 'product_code', label: 'Product Code (SKU)' },
  { value: 'manufacturer_part_number', label: 'Manufacturer Part Number (MPN)' },
];

// ── Import modal ─────────────────────────────────────────────────────────────
function ImportProductsModal({ listId, onClose, onImported }) {
  const [step, setStep] = useState(1); // 1=upload 2=map 3=results
  const [file, setFile] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [columns, setColumns] = useState([]);
  const [previewRows, setPreviewRows] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [selectedColumn, setSelectedColumn] = useState('');
  const [selectedField, setSelectedField] = useState('product_code');
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const handleFile = async (f) => {
    if (!f) return;
    const ext = f.name.split('.').pop().toLowerCase();
    if (!['csv', 'xlsx', 'xls'].includes(ext)) {
      setError('Only CSV, XLSX, or XLS files are supported.');
      return;
    }
    setError('');
    setFile(f);
    setPreviewing(true);
    try {
      const data = await previewImportFile(f);
      setColumns(data.columns || []);
      setPreviewRows(data.preview || []);
      setTotalRows(data.total_rows || 0);
      setSelectedColumn(data.columns?.[0] || '');
      setStep(2);
    } catch (err) {
      setError(err.message);
    } finally {
      setPreviewing(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files?.[0];
    if (f) handleFile(f);
  };

  const handleImport = async () => {
    if (!selectedColumn) return;
    setImporting(true);
    setError('');
    try {
      const data = await importProductListItems(listId, file, selectedColumn, selectedField);
      setResult(data);
      setStep(3);
      onImported();
    } catch (err) {
      setError(err.message);
    } finally {
      setImporting(false);
    }
  };

  const colStyle = (isHeader) => ({
    padding: '5px 10px', fontSize: 11, textAlign: 'left',
    borderBottom: '1px solid #f3f4f6',
    background: isHeader ? '#f9fafb' : '#fff',
    fontWeight: isHeader ? 700 : 400,
    color: isHeader ? '#374151' : '#111827',
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 160,
  });

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1050,
      background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        background: '#fff', borderRadius: 14, boxShadow: '0 20px 60px rgba(0,0,0,.18)',
        width: '100%', maxWidth: 620, maxHeight: '90vh', display: 'flex', flexDirection: 'column',
      }}>
        {/* Header */}
        <div style={{ padding: '18px 22px 14px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', gap: 10 }}>
          <i className="bi bi-file-earmark-arrow-up" style={{ fontSize: 20, color: '#1a56db' }} aria-hidden="true" />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>Import Products from File</div>
            <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 1 }}>
              Step {step} of 3 — {step === 1 ? 'Upload File' : step === 2 ? 'Map Columns' : 'Results'}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 18, color: '#9ca3af', cursor: 'pointer', lineHeight: 1, padding: 4 }} aria-label="Close">
            <i className="bi bi-x-lg" />
          </button>
        </div>

        {/* Step progress */}
        <div style={{ display: 'flex', padding: '10px 22px 0', gap: 4 }}>
          {[1, 2, 3].map(s => (
            <div key={s} style={{
              flex: 1, height: 3, borderRadius: 99,
              background: s <= step ? '#1a56db' : '#e5e7eb',
              transition: 'background .2s',
            }} />
          ))}
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 22px' }}>
          {error && (
            <div style={{ background: '#fee2e2', color: '#991b1b', fontSize: 12, padding: '8px 12px', borderRadius: 7, marginBottom: 14 }}>
              <i className="bi bi-exclamation-circle me-1" />
              {error}
            </div>
          )}

          {/* Step 1: Upload */}
          {step === 1 && (
            <div>
              <div
                onDragOver={e => { e.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                style={{
                  border: `2px dashed ${dragging ? '#1a56db' : '#d1d5db'}`,
                  borderRadius: 12, padding: '48px 24px', textAlign: 'center', cursor: 'pointer',
                  background: dragging ? '#eff6ff' : '#fafafa', transition: 'all .15s',
                }}
              >
                <i className="bi bi-cloud-upload" style={{ fontSize: 40, color: dragging ? '#1a56db' : '#9ca3af', display: 'block', marginBottom: 10 }} aria-hidden="true" />
                <div style={{ fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 4 }}>
                  {previewing ? 'Reading file…' : 'Drop your file here or click to browse'}
                </div>
                <div style={{ fontSize: 12, color: '#9ca3af' }}>Supports .csv, .xlsx, .xls</div>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,.xls"
                style={{ display: 'none' }}
                onChange={e => handleFile(e.target.files?.[0])}
              />
            </div>
          )}

          {/* Step 2: Map columns */}
          {step === 2 && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 12, color: '#6b7280' }}>
                  <i className="bi bi-file-earmark-text me-1" />
                  <strong>{file?.name}</strong>
                </span>
                <span style={{ fontSize: 12, background: '#eff6ff', color: '#1a56db', padding: '2px 8px', borderRadius: 99, fontWeight: 600 }}>
                  {totalRows.toLocaleString()} rows
                </span>
              </div>

              {/* Preview table */}
              {previewRows.length > 0 && (
                <div style={{ marginBottom: 18 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: '#9ca3af', marginBottom: 6 }}>Preview (first {previewRows.length} rows)</div>
                  <div style={{ overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: 8 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                      <thead>
                        <tr>
                          {columns.map(c => (
                            <th key={c} style={{ ...colStyle(true), background: selectedColumn === c ? '#eff6ff' : '#f9fafb', color: selectedColumn === c ? '#1a56db' : '#374151' }}>
                              {selectedColumn === c && <i className="bi bi-check2 me-1" style={{ fontSize: 10 }} />}
                              {c}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {previewRows.map((row, i) => (
                          <tr key={i}>
                            {columns.map(c => (
                              <td key={c} style={{ ...colStyle(false), background: selectedColumn === c ? '#fafeff' : '#fff' }}>
                                {row[c] || <span style={{ color: '#d1d5db' }}>—</span>}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Mapping controls */}
              <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: '14px 16px' }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 12 }}>
                  <i className="bi bi-sliders me-1" style={{ color: '#1a56db' }} />
                  Field Mapping
                </div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 160 }}>
                    <label style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 4 }}>
                      Column in your file
                    </label>
                    <select
                      className="form-select form-select-sm"
                      value={selectedColumn}
                      onChange={e => setSelectedColumn(e.target.value)}
                    >
                      {columns.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div style={{ fontSize: 18, color: '#9ca3af', paddingTop: 18 }}>→</div>
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <label style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 4 }}>
                      Match against product field
                    </label>
                    <select
                      className="form-select form-select-sm"
                      value={selectedField}
                      onChange={e => setSelectedField(e.target.value)}
                    >
                      {IMPORT_FIELD_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </div>
                </div>
                <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 10 }}>
                  <i className="bi bi-info-circle me-1" />
                  Values in the selected column will be looked up in the product catalog. Matched products will be added to this list.
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Results */}
          {step === 3 && result && (
            <div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', background: '#d1fae5', borderRadius: 10 }}>
                  <i className="bi bi-check-circle-fill" style={{ fontSize: 22, color: '#059669', flexShrink: 0 }} aria-hidden="true" />
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#065f46' }}>{result.added_count} product{result.added_count !== 1 ? 's' : ''} added to list</div>
                    <div style={{ fontSize: 12, color: '#047857' }}>Successfully matched and added from {result.total_rows} rows</div>
                  </div>
                </div>
                {result.already_present_count > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', background: '#eff6ff', borderRadius: 10 }}>
                    <i className="bi bi-info-circle-fill" style={{ fontSize: 18, color: '#1a56db', flexShrink: 0 }} aria-hidden="true" />
                    <div style={{ fontSize: 13, color: '#1e40af' }}>
                      {result.already_present_count} product{result.already_present_count !== 1 ? 's were' : ' was'} already in the list (skipped)
                    </div>
                  </div>
                )}
                {result.not_found.length > 0 && (
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '10px 16px', background: '#fff7ed', borderRadius: 10 }}>
                    <i className="bi bi-exclamation-triangle-fill" style={{ fontSize: 18, color: '#d97706', flexShrink: 0, marginTop: 2 }} aria-hidden="true" />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#92400e', marginBottom: 6 }}>
                        {result.not_found.length} row{result.not_found.length !== 1 ? 's' : ''} could not be matched
                      </div>
                      <div style={{ maxHeight: 160, overflowY: 'auto', border: '1px solid #fde68a', borderRadius: 6, background: '#fff' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                          <thead>
                            <tr>
                              <th style={{ padding: '4px 10px', background: '#fef9c3', fontWeight: 700, color: '#78350f', textAlign: 'left', width: 60 }}>Row</th>
                              <th style={{ padding: '4px 10px', background: '#fef9c3', fontWeight: 700, color: '#78350f', textAlign: 'left' }}>Value ({selectedColumn})</th>
                            </tr>
                          </thead>
                          <tbody>
                            {result.not_found.map((nf, i) => (
                              <tr key={i} style={{ borderTop: '1px solid #fde68a' }}>
                                <td style={{ padding: '3px 10px', color: '#6b7280' }}>{nf.row}</td>
                                <td style={{ padding: '3px 10px', color: '#374151', fontFamily: 'monospace' }}>{nf.value}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 22px', borderTop: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {step === 3 ? (
            <>
              <span style={{ fontSize: 12, color: '#9ca3af' }}>{result?.total_rows} rows processed</span>
              <button className="btn btn-primary" style={{ background: '#1a56db', borderColor: '#1a56db' }} onClick={onClose}>
                <i className="bi bi-check2 me-1" />Done
              </button>
            </>
          ) : (
            <>
              <button className="btn btn-outline-secondary btn-sm" onClick={step === 1 ? onClose : () => setStep(1)}>
                {step === 1 ? 'Cancel' : <><i className="bi bi-arrow-left me-1" />Back</>}
              </button>
              {step === 2 && (
                <button
                  className="btn btn-primary btn-sm"
                  style={{ background: '#1a56db', borderColor: '#1a56db' }}
                  onClick={handleImport}
                  disabled={importing || !selectedColumn}
                >
                  {importing
                    ? <><span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />Importing…</>
                    : <><i className="bi bi-upload me-1" />Import {totalRows.toLocaleString()} rows</>
                  }
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Product card (grid tile) ─────────────────────────────────────────────────
function ProductCard({ product, selected, onToggle }) {
  const [imgErr, setImgErr] = useState(false);
  return (
    <div
      role="checkbox"
      aria-checked={selected}
      tabIndex={0}
      onClick={onToggle}
      onKeyDown={e => (e.key === ' ' || e.key === 'Enter') && onToggle()}
      style={{
        position: 'relative', borderRadius: 10, cursor: 'pointer', overflow: 'hidden',
        border: `2px solid ${selected ? '#1a56db' : '#e5e7eb'}`,
        background: '#fff',
        boxShadow: selected ? '0 0 0 3px rgba(26,86,219,.13)' : '0 1px 3px rgba(0,0,0,.06)',
        transition: 'border-color .15s, box-shadow .15s',
        display: 'flex', flexDirection: 'column',
      }}
    >
      {/* Selection badge */}
      <div style={{
        position: 'absolute', top: 8, left: 8, zIndex: 2,
        width: 22, height: 22, borderRadius: 6,
        border: `2px solid ${selected ? '#1a56db' : '#d1d5db'}`,
        background: selected ? '#1a56db' : 'rgba(255,255,255,.9)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        transition: 'all .12s',
      }}>
        {selected && <i className="bi bi-check" style={{ color: '#fff', fontSize: 13, lineHeight: 1 }} aria-hidden="true" />}
      </div>

      {/* Image area */}
      <div style={{
        width: '100%', aspectRatio: '1/1', background: '#f9fafb',
        display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
      }}>
        {product.image_url && !imgErr
          ? <img
              src={product.image_url}
              alt={product.name}
              style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 10 }}
              onError={() => setImgErr(true)}
              loading="lazy"
            />
          : <i className="bi bi-box-seam" style={{ fontSize: 36, color: '#d1d5db' }} aria-hidden="true" />
        }
      </div>

      {/* Info */}
      <div style={{ padding: '10px 10px 12px', flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {product.category && (
          <span style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.05em', color: '#6366f1', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {product.category}
          </span>
        )}
        <div style={{ fontSize: 12, fontWeight: 700, color: '#111827', lineHeight: 1.35,
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
          {product.name || product.product_code}
        </div>
        {product.manufacturer && (
          <div style={{ fontSize: 11, color: '#6b7280', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {product.manufacturer}
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 4, gap: 4 }}>
          <code style={{ fontSize: 10, color: '#9ca3af', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            #{product.part_number || product.product_code}
          </code>
          {product.price > 0 && (
            <span style={{ fontSize: 12, fontWeight: 700, color: '#059669', flexShrink: 0 }}>
              ${Number(product.price).toFixed(2)}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Product picker panel ─────────────────────────────────────────────────────
function ProductPicker({ listId, currentItems, onSaved }) {
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('');
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 24;
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [facets, setFacets] = useState({ categories: [] });
  const [searching, setSearching] = useState(false);
  // Ordered array — position = sort_order sent to backend on save
  const [selected, setSelected] = useState(() => (currentItems || []).map(i => i.product_code));
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [showImport, setShowImport] = useState(false);
  const searchTimer = useRef(null);

  // Accumulate full product data from search results + currentItems for filter derivation
  const [productData, setProductData] = useState(() => {
    const m = {};
    (currentItems || []).forEach(item => {
      m[item.product_code] = {
        product_code: item.product_code,
        name: item.product_name || item.product_code,
        category: item.category || '',
        image_url: item.image_url || '',
      };
    });
    return m;
  });

  // List-items filter (right panel)
  const [listFilter, setListFilter] = useState({ category: '', manufacturer: '' });

  // Derived set for O(1) lookup while keeping array order
  const selectedSet = React.useMemo(() => new Set(selected), [selected]);

  // Keep `selected` in sync when currentItems change from outside
  useEffect(() => {
    setSelected((currentItems || []).map(i => i.product_code));
    // Seed productData from currentItems (has category from DB join)
    setProductData(prev => {
      const next = { ...prev };
      (currentItems || []).forEach(item => {
        if (!next[item.product_code]) {
          next[item.product_code] = {
            product_code: item.product_code,
            name: item.product_name || item.product_code,
            category: item.category || '',
            image_url: item.image_url || '',
          };
        }
      });
      return next;
    });
  }, [currentItems]);

  // Accumulate full product data (with manufacturer) from Solr search results
  useEffect(() => {
    if (!results.length) return;
    setProductData(prev => {
      const next = { ...prev };
      results.forEach(r => { next[r.product_code] = r; });
      return next;
    });
  }, [results]);

  const [selectingAll, setSelectingAll] = useState(false);

  const doSearch = useCallback((q, cat, pg) => {
    setSearching(true);
    searchProducts({ q, category: cat, page: pg, pageSize: PAGE_SIZE })
      .then(res => {
        setResults(res?.products || []);
        setTotal(res?.total || 0);
        setFacets(res?.facets || { categories: [] });
      })
      .catch(() => setResults([]))
      .finally(() => setSearching(false));
  }, []);

  // Initial load — show all products
  useEffect(() => { doSearch('', '', 1); }, [doSearch]);

  const handleQueryChange = (v) => {
    setQuery(v);
    setPage(1);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => doSearch(v, category, 1), 350);
  };

  const handleCategoryChange = (v) => {
    setCategory(v);
    setPage(1);
    doSearch(query, v, 1);
  };

  const handlePage = (p) => {
    setPage(p);
    doSearch(query, category, p);
  };

  const toggleProduct = (code) => {
    setSelected(prev =>
      prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]
    );
  };

  const removeProduct = (code) => {
    setSelected(prev => prev.filter(c => c !== code));
  };

  const moveProductUp = (index) => {
    setSelected(prev => {
      if (index === 0) return prev;
      const next = [...prev];
      [next[index - 1], next[index]] = [next[index], next[index - 1]];
      return next;
    });
  };

  const moveProductDown = (index) => {
    setSelected(prev => {
      if (index >= prev.length - 1) return prev;
      const next = [...prev];
      [next[index], next[index + 1]] = [next[index + 1], next[index]];
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveMsg('');
    try {
      await setProductListItems(listId, selected);
      setSaveMsg('Saved!');
      onSaved();
      setTimeout(() => setSaveMsg(''), 2500);
    } catch (err) {
      setSaveMsg(`Error: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Page-level select-all state
  const pageSelectedCount = results.filter(p => selectedSet.has(p.product_code)).length;
  const allPageSelected = results.length > 0 && pageSelectedCount === results.length;
  const somePageSelected = pageSelectedCount > 0 && !allPageSelected;

  const handleSelectPage = () => {
    if (allPageSelected) {
      const toRemove = new Set(results.map(p => p.product_code));
      setSelected(prev => prev.filter(c => !toRemove.has(c)));
    } else {
      setProductData(pd => {
        const upd = { ...pd };
        results.forEach(p => { upd[p.product_code] = p; });
        return upd;
      });
      setSelected(prev => {
        const prevSet = new Set(prev);
        const toAdd = results.map(p => p.product_code).filter(c => !prevSet.has(c));
        return [...prev, ...toAdd];
      });
    }
  };

  // "Select all results" fetches every matching code across all pages
  const handleSelectAllResults = async () => {
    setSelectingAll(true);
    try {
      const BATCH = 300;
      let pg = 1;
      let collected = [];
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const res = await searchProducts({ q: query, category, page: pg, pageSize: BATCH });
        const prods = res?.products || [];
        collected = collected.concat(prods);
        if (collected.length >= (res?.total || 0) || prods.length < BATCH) break;
        pg++;
      }
      setProductData(pd => {
        const upd = { ...pd };
        collected.forEach(p => { upd[p.product_code] = p; });
        return upd;
      });
      setSelected(prev => {
        const prevSet = new Set(prev);
        const toAdd = collected.map(p => p.product_code).filter(c => !prevSet.has(c));
        return [...prev, ...toAdd];
      });
    } catch {
      // silently ignore
    } finally {
      setSelectingAll(false);
    }
  };

  const selectedItems = selected;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  // Derive filter facets from the items actually in the list (using accumulated product data)
  const listFacets = React.useMemo(() => {
    const cats = {};
    const brands = {};
    selectedItems.forEach(code => {
      const d = productData[code];
      if (d?.category) cats[d.category] = (cats[d.category] || 0) + 1;
      if (d?.manufacturer) brands[d.manufacturer] = (brands[d.manufacturer] || 0) + 1;
    });
    return {
      categories: Object.entries(cats).map(([v, c]) => ({ value: v, count: c })).sort((a, b) => b.count - a.count),
      manufacturers: Object.entries(brands).map(([v, c]) => ({ value: v, count: c })).sort((a, b) => b.count - a.count),
    };
  }, [selectedItems, productData]);

  // Items filtered by the right-panel list filter
  const filteredSelectedItems = React.useMemo(() => {
    if (!listFilter.category && !listFilter.manufacturer) return selectedItems;
    return selectedItems.filter(code => {
      const d = productData[code];
      if (listFilter.category && d?.category !== listFilter.category) return false;
      if (listFilter.manufacturer && d?.manufacturer !== listFilter.manufacturer) return false;
      return true;
    });
  }, [selectedItems, listFilter, productData]);

  return (
    <>
    {showImport && (
      <ImportProductsModal
        listId={listId}
        onClose={() => setShowImport(false)}
        onImported={() => { onSaved(); setShowImport(false); }}
      />
    )}
    <div style={{ display: 'flex', gap: 0, border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden', minHeight: 480 }}>

      {/* ── Left: search & results ── */}
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', borderRight: '1px solid #e5e7eb' }}>
        {/* Search toolbar */}
        <div style={{ background: '#fafafa', borderBottom: '1px solid #f3f4f6' }}>
          <div style={{ padding: '10px 14px', display: 'flex', gap: 8, alignItems: 'center' }}>
            <div style={{ position: 'relative', flex: 1 }}>
              <i className="bi bi-search" style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', fontSize: 13, pointerEvents: 'none' }} aria-hidden="true" />
              <input
                className="form-control form-control-sm"
                style={{ paddingLeft: 30, paddingRight: query ? 28 : undefined }}
                placeholder="Search products by name, code, brand…"
                value={query}
                onChange={e => handleQueryChange(e.target.value)}
                aria-label="Search products"
              />
              {query && (
                <button style={{ position: 'absolute', right: 7, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 0, fontSize: 15 }}
                  onClick={() => handleQueryChange('')} aria-label="Clear">
                  <i className="bi bi-x" />
                </button>
              )}
            </div>
            <button
              onClick={() => setShowImport(true)}
              title="Import from Excel or CSV"
              style={{
                display: 'flex', alignItems: 'center', gap: 5, padding: '4px 11px', borderRadius: 7,
                fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0, cursor: 'pointer',
                background: '#fff', color: '#374151', border: '1.5px solid #d1d5db',
                transition: 'all .12s',
              }}
            >
              <i className="bi bi-file-earmark-arrow-up" style={{ fontSize: 13 }} aria-hidden="true" />
              Import
            </button>
            {searching && <div className="spinner-border spinner-border-sm text-primary" role="status" aria-label="Searching" />}
            {!searching && <span style={{ fontSize: 11, color: '#9ca3af', whiteSpace: 'nowrap' }}>{total.toLocaleString()} found</span>}
          </div>

          {/* Select-all toolbar */}
          {results.length > 0 && !searching && (
            <div style={{ padding: '4px 14px 6px', display: 'flex', alignItems: 'center', gap: 8, borderTop: '1px solid #f3f4f6' }}>
              {/* Indeterminate-style checkbox */}
              <button
                onClick={handleSelectPage}
                aria-label={allPageSelected ? 'Deselect all on this page' : 'Select all on this page'}
                title={allPageSelected ? 'Deselect page' : 'Select all on page'}
                style={{
                  width: 20, height: 20, borderRadius: 5, flexShrink: 0, cursor: 'pointer',
                  border: `2px solid ${allPageSelected ? '#1a56db' : somePageSelected ? '#1a56db' : '#d1d5db'}`,
                  background: allPageSelected ? '#1a56db' : 'transparent',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  padding: 0, position: 'relative',
                }}
              >
                {allPageSelected && <i className="bi bi-check" style={{ color: '#fff', fontSize: 12, lineHeight: 1 }} aria-hidden="true" />}
                {somePageSelected && (
                  <span style={{ width: 8, height: 2, background: '#1a56db', borderRadius: 1, display: 'block' }} aria-hidden="true" />
                )}
              </button>
              <span style={{ fontSize: 12, color: '#374151' }}>
                {allPageSelected
                  ? <><strong>{results.length}</strong> on this page selected — click to deselect</>
                  : somePageSelected
                    ? <><strong>{pageSelectedCount}</strong> of {results.length} on this page selected</>
                    : <>Select all <strong>{results.length}</strong> on this page</>
                }
              </span>
              {/* "Select ALL results" offer when there are multiple pages */}
              {totalPages > 1 && (
                <button
                  onClick={handleSelectAllResults}
                  disabled={selectingAll}
                  style={{
                    marginLeft: 'auto', padding: '2px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                    background: '#eff6ff', color: '#1a56db', border: '1px solid #bfdbfe', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0,
                  }}
                >
                  {selectingAll
                    ? <><span className="spinner-border spinner-border-sm" style={{ width: 11, height: 11, borderWidth: 1.5 }} role="status" aria-hidden="true" /> Selecting…</>
                    : <><i className="bi bi-check2-all" aria-hidden="true" /> Select all {total.toLocaleString()} results</>
                  }
                </button>
              )}
            </div>
          )}

          {/* Category chips derived from Solr facets for the search */}
          {facets.categories?.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 14px 10px', overflowX: 'auto', scrollbarWidth: 'none' }}>
              <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: '#9ca3af', flexShrink: 0, width: 56 }}>Category</span>
              <div style={{ display: 'flex', gap: 5, flexWrap: 'nowrap', overflow: 'auto', scrollbarWidth: 'none' }}>
                {facets.categories.map(c => (
                  <button
                    key={c.value}
                    onClick={() => handleCategoryChange(category === c.value ? '' : c.value)}
                    aria-pressed={category === c.value}
                    style={{
                      padding: '3px 10px', borderRadius: 99, whiteSpace: 'nowrap', fontSize: 11, flexShrink: 0,
                      background: category === c.value ? '#1a56db' : '#fff',
                      color: category === c.value ? '#fff' : '#374151',
                      border: `1.5px solid ${category === c.value ? '#1a56db' : '#e5e7eb'}`,
                      cursor: 'pointer', fontWeight: category === c.value ? 700 : 400, transition: 'all .12s',
                    }}
                  >
                    {c.value} <span style={{ opacity: .65 }}>{c.count}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Results grid */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '12px 14px' }}>
          {searching && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 10 }}>
              {Array.from({ length: 12 }).map((_, i) => (
                <div key={i} style={{ borderRadius: 10, background: '#f3f4f6', aspectRatio: '4/5', animation: 'pulse 1.5s ease-in-out infinite' }} />
              ))}
            </div>
          )}
          {!searching && results.length === 0 && (
            <div style={{ textAlign: 'center', padding: '48px 0', color: '#9ca3af' }}>
              <i className="bi bi-search" style={{ fontSize: 32, display: 'block', marginBottom: 10 }} aria-hidden="true" />
              No products found{query ? ` for "${query}"` : ''}.
            </div>
          )}
          {!searching && results.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 10 }}>
              {results.map(prod => (
                <ProductCard
                  key={prod.product_code}
                  product={prod}
                  selected={selectedSet.has(prod.product_code)}
                  onToggle={() => toggleProduct(prod.product_code)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{ padding: '8px 12px', borderTop: '1px solid #f3f4f6', display: 'flex', gap: 4, justifyContent: 'center', flexWrap: 'wrap', background: '#fafafa' }}>
            <button className="btn btn-sm btn-outline-secondary" style={{ padding: '2px 8px' }} disabled={page <= 1} onClick={() => handlePage(page - 1)} aria-label="Prev page">
              <i className="bi bi-chevron-left" />
            </button>
            {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
              const p = page <= 4 ? i + 1 : page - 3 + i;
              if (p < 1 || p > totalPages) return null;
              return (
                <button key={p} className={`btn btn-sm ${p === page ? 'btn-primary' : 'btn-outline-secondary'}`}
                  style={{ padding: '2px 9px', ...(p === page ? { background: '#1a56db', borderColor: '#1a56db' } : {}) }}
                  onClick={() => handlePage(p)}>{p}
                </button>
              );
            })}
            <button className="btn btn-sm btn-outline-secondary" style={{ padding: '2px 8px' }} disabled={page >= totalPages} onClick={() => handlePage(page + 1)} aria-label="Next page">
              <i className="bi bi-chevron-right" />
            </button>
          </div>
        )}
      </div>

      {/* ── Right: items in this list ── */}
      <div style={{ width: 300, flexShrink: 0, display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <div style={{ padding: '10px 14px 8px', background: '#fafafa', borderBottom: '1px solid #f3f4f6' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#374151' }}>
              <i className="bi bi-bag-check me-1" style={{ color: '#1a56db' }} aria-hidden="true" />
              In this list ({selectedItems.length})
              {filteredSelectedItems.length < selectedItems.length && (
                <span style={{ fontWeight: 400, color: '#6b7280', marginLeft: 4 }}>· showing {filteredSelectedItems.length}</span>
              )}
            </span>
            {selectedItems.length > 0 && (
              <button style={{ background: 'none', border: 'none', fontSize: 12, color: '#ef4444', cursor: 'pointer', padding: 0 }}
                onClick={() => setSelected([])}>
                Clear all
              </button>
            )}
          </div>

          {/* Filter chips derived from items in the list */}
          {listFacets.categories.length > 0 && (
            <div style={{ marginBottom: 4 }}>
              <div style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: '#9ca3af', marginBottom: 3 }}>Filter by category</div>
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {listFacets.categories.map(f => (
                  <button
                    key={f.value}
                    onClick={() => setListFilter(prev => ({ ...prev, category: prev.category === f.value ? '' : f.value }))}
                    aria-pressed={listFilter.category === f.value}
                    style={{
                      padding: '2px 9px', borderRadius: 99, fontSize: 11, whiteSpace: 'nowrap',
                      background: listFilter.category === f.value ? '#1a56db' : '#fff',
                      color: listFilter.category === f.value ? '#fff' : '#374151',
                      border: `1.5px solid ${listFilter.category === f.value ? '#1a56db' : '#e5e7eb'}`,
                      cursor: 'pointer', fontWeight: listFilter.category === f.value ? 700 : 400,
                    }}
                  >
                    {f.value} <span style={{ opacity: .65 }}>{f.count}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          {listFacets.manufacturers.length > 0 && (
            <div>
              <div style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: '#9ca3af', marginBottom: 3 }}>Filter by brand</div>
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {listFacets.manufacturers.map(f => (
                  <button
                    key={f.value}
                    onClick={() => setListFilter(prev => ({ ...prev, manufacturer: prev.manufacturer === f.value ? '' : f.value }))}
                    aria-pressed={listFilter.manufacturer === f.value}
                    style={{
                      padding: '2px 9px', borderRadius: 99, fontSize: 11, whiteSpace: 'nowrap',
                      background: listFilter.manufacturer === f.value ? '#0891b2' : '#fff',
                      color: listFilter.manufacturer === f.value ? '#fff' : '#374151',
                      border: `1.5px solid ${listFilter.manufacturer === f.value ? '#0891b2' : '#e5e7eb'}`,
                      cursor: 'pointer', fontWeight: listFilter.manufacturer === f.value ? 700 : 400,
                    }}
                  >
                    {f.value} <span style={{ opacity: .65 }}>{f.count}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          {(listFilter.category || listFilter.manufacturer) && (
            <button onClick={() => setListFilter({ category: '', manufacturer: '' })}
              style={{ marginTop: 6, background: 'none', border: 'none', fontSize: 11, color: '#3b82f6', cursor: 'pointer', padding: 0 }}>
              <i className="bi bi-x-circle me-1" />Clear filters
            </button>
          )}
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '8px 10px' }}>
          {selectedItems.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: '#d1d5db' }}>
              <i className="bi bi-grid" style={{ fontSize: 36, display: 'block', marginBottom: 10 }} aria-hidden="true" />
              <span style={{ fontSize: 13 }}>No products selected.<br />Click cards on the left.</span>
            </div>
          ) : filteredSelectedItems.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '32px 12px', color: '#9ca3af' }}>
              <i className="bi bi-funnel" style={{ fontSize: 28, display: 'block', marginBottom: 8 }} />
              <span style={{ fontSize: 12 }}>No items match the active filter.</span>
            </div>
          ) : (
            filteredSelectedItems.map((code, filteredIndex) => {
              const globalIndex = selectedItems.indexOf(code);
              const prod = results.find(p => p.product_code === code) || productData[code] || null;
              const existing = (currentItems || []).find(i => i.product_code === code);
              const imgUrl = prod?.image_url || existing?.image_url;
              const isFiltered = filteredSelectedItems.length < selectedItems.length;
              return (
                <div key={code} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 0', borderBottom: '1px solid #f3f4f6' }}>
                  {/* Move up/down — only show when no filter active (order is meaningful) */}
                  {!isFiltered && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 1, flexShrink: 0 }}>
                      <button
                        className="btn btn-sm"
                        style={{ padding: '0 4px', border: 'none', background: 'none', color: globalIndex === 0 ? '#d1d5db' : '#6b7280', lineHeight: 1 }}
                        onClick={() => moveProductUp(globalIndex)}
                        disabled={globalIndex === 0}
                        aria-label="Move up"
                      >
                        <i className="bi bi-chevron-up" style={{ fontSize: 10 }} />
                      </button>
                      <button
                        className="btn btn-sm"
                        style={{ padding: '0 4px', border: 'none', background: 'none', color: globalIndex === selectedItems.length - 1 ? '#d1d5db' : '#6b7280', lineHeight: 1 }}
                        onClick={() => moveProductDown(globalIndex)}
                        disabled={globalIndex === selectedItems.length - 1}
                        aria-label="Move down"
                      >
                        <i className="bi bi-chevron-down" style={{ fontSize: 10 }} />
                      </button>
                    </div>
                  )}
                  {/* Thumbnail */}
                  <div style={{
                    width: 36, height: 36, flexShrink: 0, borderRadius: 6, overflow: 'hidden',
                    background: '#f3f4f6', border: '1px solid #e5e7eb',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {imgUrl
                      ? <img src={imgUrl} alt="" style={{ width: 36, height: 36, objectFit: 'contain', padding: 3 }} loading="lazy" />
                      : <i className="bi bi-box-seam" style={{ fontSize: 14, color: '#d1d5db' }} aria-hidden="true" />
                    }
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {prod?.name || existing?.product_name || code}
                    </div>
                    <code style={{ fontSize: 10, color: '#9ca3af' }}>{code}</code>
                  </div>
                  <button
                    className="btn btn-sm"
                    style={{ padding: '1px 6px', color: '#ef4444', border: '1px solid #fca5a5', borderRadius: 5, background: '#fff3f3', flexShrink: 0 }}
                    onClick={() => removeProduct(code)}
                    aria-label={`Remove ${code}`}
                  >
                    <i className="bi bi-x" aria-hidden="true" />
                  </button>
                </div>
              );
            })
          )}
        </div>

        {/* Save footer */}
        <div style={{ padding: '12px 14px', borderTop: '1px solid #e5e7eb', background: '#fafafa' }}>
          {saveMsg && (
            <div style={{
              fontSize: 12, fontWeight: 600, marginBottom: 8, padding: '5px 10px', borderRadius: 6,
              background: saveMsg.startsWith('Error') ? '#fee2e2' : '#d1fae5',
              color: saveMsg.startsWith('Error') ? '#991b1b' : '#065f46',
            }}>
              <i className={`bi ${saveMsg.startsWith('Error') ? 'bi-x-circle' : 'bi-check-circle'} me-1`} aria-hidden="true" />
              {saveMsg}
            </div>
          )}
          <button
            className="btn btn-primary w-100"
            style={{ background: '#1a56db', borderColor: '#1a56db', fontSize: 13 }}
            onClick={handleSave}
            disabled={saving}
          >
            {saving
              ? <><span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />Saving…</>
              : <><i className="bi bi-floppy me-1" aria-hidden="true" />Save {selectedItems.length} Product{selectedItems.length !== 1 ? 's' : ''}</>
            }
          </button>
        </div>
      </div>
    </div>
    </>
  );
}

// ── Main tab ─────────────────────────────────────────────────────────────────
export default function ProductListsTab({ productLists, loading, onRefresh }) {
  const [createForm, setCreateForm] = useState({ list_key: '', list_name: '', description: '' });
  const [createError, setCreateError] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [expandedDetail, setExpandedDetail] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [editName, setEditName] = useState({});

  const loadDetail = (id) => {
    getProductList(id).then(setExpandedDetail).catch(() => setExpandedDetail(null));
  };

  useEffect(() => {
    if (expandedId == null) { setExpandedDetail(null); return; }
    loadDetail(expandedId);
  }, [expandedId]);

  const handleCreate = async () => {
    setCreateError('');
    if (!createForm.list_key || !createForm.list_name) {
      setCreateError('Key and name are required.');
      return;
    }
    try {
      await createProductList(createForm);
      setCreateForm({ list_key: '', list_name: '', description: '' });
      onRefresh();
    } catch (err) {
      setCreateError(err.message);
    }
  };

  const handleRename = async (listId) => {
    const newName = editName[listId];
    if (!newName) return;
    try {
      await updateProductList(listId, { list_name: newName });
      setEditName(n => ({ ...n, [listId]: undefined }));
      onRefresh();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await deleteProductList(confirmDelete.id);
      setConfirmDelete(null);
      if (expandedId === confirmDelete.id) setExpandedId(null);
      onRefresh();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleExpand = (list) => {
    setExpandedId(prev => prev === list.id ? null : list.id);
  };

  if (loading) {
    return <div className="text-center py-5"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading…</span></div></div>;
  }

  return (
    <div>
      {/* Create */}
      <div className="card mb-4" style={{ borderRadius: 10, border: '1px solid #e5e7eb' }}>
        <div className="card-body" style={{ padding: '18px 20px' }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 14, color: '#374151' }}>Create Product List</h3>
          <div className="row g-3 align-items-end">
            <div className="col-sm-3">
              <label className="form-label fw-semibold" htmlFor="pl-key">List Key</label>
              <input id="pl-key" className="form-control" value={createForm.list_key} onChange={e => setCreateForm(f => ({ ...f, list_key: e.target.value }))} placeholder="ppe-2024" />
            </div>
            <div className="col-sm-4">
              <label className="form-label fw-semibold" htmlFor="pl-name">List Name</label>
              <input id="pl-name" className="form-control" value={createForm.list_name} onChange={e => setCreateForm(f => ({ ...f, list_name: e.target.value }))} placeholder="PPE 2024 Items" />
            </div>
            <div className="col-sm-3">
              <label className="form-label fw-semibold" htmlFor="pl-desc">Description</label>
              <input id="pl-desc" className="form-control" value={createForm.description} onChange={e => setCreateForm(f => ({ ...f, description: e.target.value }))} />
            </div>
            <div className="col-sm-2">
              <button className="btn btn-primary w-100" onClick={handleCreate} style={{ background: '#1a56db', borderColor: '#1a56db' }}>
                <i className="bi bi-plus-lg me-1" aria-hidden="true" />Create
              </button>
            </div>
          </div>
          {createError && <p style={{ color: '#c81e1e', fontSize: 13, marginTop: 8, marginBottom: 0 }}>{createError}</p>}
        </div>
      </div>

      {productLists.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '48px 24px', color: '#6b7280' }}>No product lists yet. Create one above.</div>
      ) : (
        <div className="d-flex flex-column gap-2">
          {productLists.map(list => (
            <div key={list.id} className="card" style={{ borderRadius: 10, border: '1px solid #e5e7eb' }}>
              {/* Header row */}
              <div
                className="card-header"
                style={{ background: 'transparent', display: 'flex', alignItems: 'center', gap: 10, padding: '14px 18px', cursor: 'pointer', borderBottom: expandedId === list.id ? '1px solid #e5e7eb' : 'none' }}
                onClick={() => handleExpand(list)}
              >
                <span style={{ fontSize: 14, fontWeight: 700, flex: 1, color: '#111827' }}>
                  {editName[list.id] !== undefined ? (
                    <input
                      className="form-control form-control-sm d-inline"
                      style={{ width: 220 }}
                      value={editName[list.id]}
                      onClick={e => e.stopPropagation()}
                      onChange={e => setEditName(n => ({ ...n, [list.id]: e.target.value }))}
                      onKeyDown={e => { if (e.key === 'Enter') handleRename(list.id); if (e.key === 'Escape') setEditName(n => ({ ...n, [list.id]: undefined })); }}
                      autoFocus
                    />
                  ) : list.list_name}
                </span>
                <code style={{ fontSize: 11, color: '#6b7280' }}>{list.list_key}</code>
                <span style={{ background: '#eff6ff', color: '#1a56db', fontSize: 12, fontWeight: 600, padding: '2px 10px', borderRadius: 99 }}>
                  {list.item_count || 0} products
                </span>
                <span style={{ color: list.is_active ? '#057a55' : '#9ca3af', fontSize: 12 }}>{list.is_active ? 'Active' : 'Inactive'}</span>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  style={{ fontSize: 12, width: 32 }}
                  onClick={e => { e.stopPropagation(); setEditName(n => ({ ...n, [list.id]: list.list_name })); }}
                  aria-label={`Rename ${list.list_name}`}
                  title="Rename"
                >
                  <i className="bi bi-pencil" aria-hidden="true" />
                </button>
                <button
                  className="btn btn-sm btn-outline-danger"
                  style={{ fontSize: 12, width: 32 }}
                  onClick={e => { e.stopPropagation(); setConfirmDelete(list); }}
                  aria-label={`Delete ${list.list_name}`}
                  title="Delete"
                >
                  <i className="bi bi-trash3" aria-hidden="true" />
                </button>
                <i className={`bi ${expandedId === list.id ? 'bi-chevron-up' : 'bi-chevron-down'}`} style={{ fontSize: 14, color: '#6b7280' }} aria-hidden="true" />
              </div>

              {/* Expanded: product picker */}
              {expandedId === list.id && (
                <div className="card-body" style={{ padding: '16px 18px' }}>
                  {!expandedDetail ? (
                    <div className="text-center py-4">
                      <div className="spinner-border spinner-border-sm text-primary" role="status" />
                    </div>
                  ) : (
                    <ProductPicker
                      listId={list.id}
                      currentItems={expandedDetail.items || []}
                      onSaved={() => { loadDetail(list.id); onRefresh(); }}
                    />
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <ConfirmPopup
        open={!!confirmDelete}
        title="Delete product list?"
        message={`"${confirmDelete?.list_name}" will be removed from all programs. This cannot be undone.`}
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete(null)}
      />
    </div>
  );
}
